import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Building2, Save, RefreshCw, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email Preview Component - Extracted to avoid re-renders
const EmailPreview = ({ settings }) => {
  const sampleData = {
    businessName: 'Ejemplo Restaurante',
    contactName: 'María García',
    quoteAmount: 497,
    quoteDescription: 'Web profesional con diseño personalizado, optimización SEO, formulario de contacto, integración con Google Maps y certificado SSL incluido.'
  };

  return (
    <div className="bg-[#f5f5f5] rounded-lg overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Email Container */}
      <div className="p-4 sm:p-6">
        <div className="max-w-[600px] mx-auto">
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', padding: '30px 24px', borderRadius: '16px 16px 0 0', textAlign: 'center' }}>
            <h1 style={{ color: 'white', margin: 0, fontSize: '20px', fontWeight: 600 }}>Tu presupuesto está listo</h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '8px 0 0 0', fontSize: '14px' }}>Web profesional para {sampleData.businessName}</p>
          </div>
          
          {/* Body */}
          <div style={{ background: 'white', padding: '28px 24px' }}>
            <p style={{ fontSize: '15px', color: '#1F2937', margin: '0 0 16px 0' }}>Hola {sampleData.contactName},</p>
            
            <p style={{ color: '#4B5563', lineHeight: 1.7, margin: '0 0 20px 0', fontSize: '14px' }}>
              Gracias por tu interés en crear una web profesional con {settings.company_name}. 
              Hemos preparado un presupuesto personalizado basado en tus necesidades.
            </p>
            
            {/* Quote Box */}
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '20px', margin: '20px 0' }}>
              <h2 style={{ color: '#166534', margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Detalle del presupuesto</h2>
              <p style={{ color: '#374151', margin: '0 0 16px 0', lineHeight: 1.7, fontSize: '13px' }}>{sampleData.quoteDescription}</p>
              <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #BBF7D0' }}>
                <span style={{ fontSize: '28px', fontWeight: 700, color: '#166534' }}>{sampleData.quoteAmount} EUR</span>
                <span style={{ color: '#6B7280', display: 'block', marginTop: '4px', fontSize: '12px' }}>IVA incluido</span>
              </div>
            </div>
            
            {/* Payment Button */}
            <div style={{ margin: '24px 0', textAlign: 'center' }}>
              <p style={{ color: '#374151', margin: '0 0 16px 0', fontSize: '14px' }}>Si todo te parece bien, puedes proceder al pago de forma segura:</p>
              <div style={{ background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', padding: '14px 36px', borderRadius: '10px', display: 'inline-block', fontWeight: 'bold', fontSize: '15px', boxShadow: '0 4px 15px rgba(16,185,129,0.3)' }}>
                Confirmar y pagar {sampleData.quoteAmount} EUR
              </div>
              <p style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>Pago 100% seguro procesado por Stripe</p>
            </div>
            
            {/* Contact Box - DYNAMIC */}
            <div style={{ marginTop: '28px', padding: '16px', background: '#F9FAFB', borderRadius: '10px' }}>
              <p style={{ margin: 0, color: '#4B5563', fontSize: '13px', lineHeight: 1.6 }}>
                ¿Tienes alguna pregunta? Estamos aquí para ayudarte.<br />
                <strong>Teléfono:</strong> <span style={{ color: '#10B981' }}>{settings.contact_phone}</span><br />
                <strong>Email:</strong> <span style={{ color: '#10B981' }}>{settings.contact_email}</span>
              </p>
            </div>
          </div>
          
          {/* Footer - DYNAMIC */}
          <div style={{ background: '#1F2937', padding: '24px', borderRadius: '0 0 16px 16px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 8px 0', color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>{settings.company_name}</p>
            <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{settings.company_tagline}</p>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '10px', lineHeight: 1.6 }}>
              {settings.contact_address}<br />
              Este email fue enviado porque solicitaste información sobre nuestros servicios.
            </p>
            <p style={{ margin: '12px 0 0 0' }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', textDecoration: 'underline', cursor: 'pointer' }}>Darme de baja de estos emails</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export function PlatformSettingsPanel() {
  const [settings, setSettings] = useState({
    contact_phone: '+34 632 664 386',
    contact_email: 'hola@lokalgrow.com',
    contact_address: 'Calle Gran Via 28, 28013 Madrid, España',
    company_name: 'LokAlGrow',
    company_tagline: 'Webs para negocios locales'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/platform-settings`);
      if (response.data.settings) {
        setSettings(response.data.settings);
        setLastUpdated(response.data.last_updated);
      }
    } catch (err) {
      console.error('Error fetching platform settings:', err);
      toast.error('Error cargando configuración');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/platform-settings`, settings, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        toast.success('Configuración guardada correctamente');
        setLastUpdated(new Date().toISOString());
      }
    } catch (err) {
      console.error('Error saving platform settings:', err);
      toast.error(err.response?.data?.detail || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Configuración de la Plataforma</h2>
          <p className="text-gray-400 text-sm mt-1">
            Estos datos aparecen en los emails automáticos de cotización
          </p>
        </div>
        {lastUpdated && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
            Última actualización: {new Date(lastUpdated).toLocaleString('es-ES')}
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-400" />
          Información de Contacto
        </h3>
        <p className="text-gray-400 text-sm mb-6">
          Esta información se incluye automáticamente en los emails de cotización de webs y en el footer para cumplir con CAN-SPAM.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Company Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Nombre de la empresa
            </label>
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="LokAlGrow"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Tagline */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">
              Eslogan / Descripción corta
            </label>
            <input
              type="text"
              value={settings.company_tagline}
              onChange={(e) => handleChange('company_tagline', e.target.value)}
              placeholder="Webs para negocios locales"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Teléfono de contacto
            </label>
            <input
              type="tel"
              value={settings.contact_phone}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
              placeholder="+34 612 345 678"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email de contacto
            </label>
            <input
              type="email"
              value={settings.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              placeholder="hola@tuempresa.com"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Address - Full width */}
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Dirección física (requerido por CAN-SPAM)
            </label>
            <input
              type="text"
              value={settings.contact_address}
              onChange={(e) => handleChange('contact_address', e.target.value)}
              placeholder="Calle Ejemplo 123, 28000 Madrid, España"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              La dirección física es obligatoria para cumplir con las leyes anti-spam (CAN-SPAM, GDPR)
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={saveSettings}
        disabled={saving}
        className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <RefreshCw className="w-5 h-5 animate-spin" />
            Guardando...
          </>
        ) : (
          <>
            <Save className="w-5 h-5" />
            Guardar Configuración
          </>
        )}
      </button>

      {/* Email Preview Section */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <button
          onClick={() => setShowEmailPreview(!showEmailPreview)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-750 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Vista Previa del Email de Cotización</h3>
              <p className="text-gray-400 text-sm">Así se verá el email que reciben los leads</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            {showEmailPreview ? (
              <>
                <span className="text-sm">Ocultar</span>
                <EyeOff className="w-5 h-5" />
              </>
            ) : (
              <>
                <span className="text-sm">Mostrar</span>
                <Eye className="w-5 h-5" />
              </>
            )}
          </div>
        </button>
        
        {showEmailPreview && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-700"
          >
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="flex-1 h-px bg-gray-700" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Previsualización en tiempo real</span>
                <div className="flex-1 h-px bg-gray-700" />
              </div>
              <EmailPreview settings={settings} />
              <p className="text-center text-xs text-gray-500 mt-4">
                Los cambios en los campos de arriba se reflejan automáticamente en esta vista previa
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-emerald-400 text-sm font-medium">¿Dónde se usan estos datos?</p>
            <ul className="text-emerald-300/80 text-sm mt-2 space-y-1">
              <li>• Emails de cotización automática enviados a leads de webs demo</li>
              <li>• Footer de emails transaccionales (CAN-SPAM compliance)</li>
              <li>• Enlace de contacto en los presupuestos</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default PlatformSettingsPanel;
